/**
 * 생성기 자리 카드 컨테이너 — 목록 끝에 만드는 중 대기 카드 또는 실패 카드 N장을 그린다.
 * 그릴 자리가 있을 때만 호출부가 마운트한다(aiGenerateDockSlotAtom 이 null 이면 그리지 않는다).
 * 생성이 시작되면 첫 대기 카드로 스크롤한다. 생성기는 화면을 잠그지 않으므로 사용자가 결과 자리를 놓치지 않게 한다.
 */
import { useEffect, useRef } from 'react';
import { useSetAtom } from 'jotai';
import { useQuizList } from '../../../components/useQuizList';
import { useAiGenerateDockCopy } from '../hooks/useAiGenerateDockCopy';
import { aiGenerateDockRunAtom, type AiGenerateDockSlot } from '../stores/aiGenerateDockRun.atoms';
import { getRemainingQuizSlots } from '../utils/aiGenerateDockCount';
import { AiGenerateDockPendingCardsUI } from './AiGenerateDockPendingCardsUI';
export const AiGenerateDockPendingCards = ({
  slot
}: {
  slot: AiGenerateDockSlot;
}) => {
  const dispatchRun = useSetAtom(aiGenerateDockRunAtom);
  const {
    quizList
  } = useQuizList();
  const {
    pendingCopy
  } = useAiGenerateDockCopy();
  const containerRef = useRef<HTMLDivElement>(null);
  // 실행이 시작된 순간의 번호 — 실행이 바뀔 때만 스크롤하려고 쓴다(목록이 바뀔 때마다 끌어가지 않게).
  const runStartNumber = slot.status === 'generating' ? slot.startNumber : undefined;
  // 카드는 지금 목록 끝에 남은 칸만큼만 — 생성·실패 중에도 문항을 고칠 수 있어, 실행 시작 때 번호·수로 그리면
  // 기존 카드와 번호가 겹치거나 20번을 넘는 자리가 보인다(결과도 지금 목록 뒤에 붙는다).
  const slotStartNumber = quizList.length + 1;
  const slotCount = Math.min(slot.count, getRemainingQuizSlots(quizList.length));
  const hasNoRoomForFailed = slot.status === 'failed' && slotCount === 0;
  useEffect(() => {
    // 남은 칸이 없으면 다시 만들 수 없으니 실패 자리를 걷는다.
    if (hasNoRoomForFailed) dispatchRun({
      type: 'dropFailed'
    });
  }, [hasNoRoomForFailed, dispatchRun]);
  useEffect(() => {
    if (runStartNumber === undefined) return;
    // 새 대기 카드가 보이게 화면 가운데로 둔다.
    containerRef.current?.scrollIntoView?.({
      behavior: 'smooth',
      block: 'center'
    });
  }, [runStartNumber]);
  if (slot.status === 'generating') {
    return <div ref={containerRef} className="w-full">
        <AiGenerateDockPendingCardsUI count={slotCount} startNumber={slotStartNumber} quizType={slot.quizType} copy={pendingCopy} />
      </div>;
  }
  if (slotCount === 0) return null;
  return <AiGenerateDockPendingCardsUI status="failed" count={slotCount} startNumber={slotStartNumber} quizType={slot.quizType} copy={pendingCopy}
  // 남은 실패 자리만큼만 다시 만든다 — 지운 자리는 다시 요청하지 않는다.
  onRetry={() => slot.retry(slotCount)}
  // 실패 자리 걷기는 사용자 행동이라 여기서 줄인다 — 실행 전이(run 훅)와는 별개다.
  onDismiss={() => dispatchRun({
    type: 'dismissFailed',
    visibleCount: slotCount
  })} data-sentry-element="AiGenerateDockPendingCardsUI" data-sentry-component="AiGenerateDockPendingCards" data-sentry-source-file="AiGenerateDockPendingCards.tsx" />;
};